import React from 'react';
import { useTranslation } from 'react-i18next';
// Its own one-key namespace, never `../../i18n`: this is in the initial bundle.
import { NS } from './i18n';
import './MetricsSkeleton.css';

/** Overview cards drawn: two rows of three, as the page lays out "All channels". */
const OVERVIEW_CARDS = [0, 1, 2, 3, 4, 5];

const Bone: React.FC<{ className: string }> = ({ className }) => (
  <span className={`metrics-skeleton__bone ${className}`} />
);

export interface MetricsSkeletonProps {
  /**
   * Adds the agent screens' 24px gutter (`AgentShell` in `AssistantDetailLayout`),
   * for `RouteSkeleton`, which draws it straight inside the app chrome.
   */
  standalone?: boolean;
}

/**
 * Loading shape of the agent's metrics (SL-1946). `RouteSkeleton` (inside the
 * app chrome), the agent layouts while the agent loads and the route's Suspense
 * fallback draw this; the dashboard then mounts with the same header card and
 * its Overview card drawing the same cards while the numbers load.
 *
 * The page as it arrives: breadcrumb, the header card (the module's title and
 * the channel select), then the Overview card: its title, the period button and
 * the metric cards (icon, label, value).
 *
 * Its own classes, never the page's: the page's sheet comes with its chunk and
 * this paints before it. Nothing visible but bones; the status carries its
 * translated text inside, visually hidden, so a screen reader announces it. No
 * `aria-busy`: on a live region it tells assistive tech to hold the
 * announcement.
 */
export const MetricsSkeleton: React.FC<MetricsSkeletonProps> = ({ standalone = false }) => {
  const { t } = useTranslation(NS);
  const text = t('loading');

  return (
    <div
      className={`metrics-skeleton${standalone ? ' metrics-skeleton--standalone' : ''}`}
      role="status"
      data-testid="metrics-skeleton"
    >
      <span className="metrics-skeleton__sr-only">{text}</span>
      <div className="metrics-skeleton__header" aria-hidden="true">
        <Bone className="metrics-skeleton__crumb" />
      </div>
      <div className="metrics-skeleton__body" aria-hidden="true">
        <div className="metrics-skeleton__card metrics-skeleton__card--head">
          <Bone className="metrics-skeleton__title" />
          <Bone className="metrics-skeleton__select" />
        </div>
        <div className="metrics-skeleton__card">
          <div className="metrics-skeleton__section-head">
            <Bone className="metrics-skeleton__section-title" />
            <Bone className="metrics-skeleton__button" />
          </div>
          <div className="metrics-skeleton__grid">
            {OVERVIEW_CARDS.map((card) => (
              <div key={card} className="metrics-skeleton__metric">
                <Bone className="metrics-skeleton__icon" />
                <Bone className="metrics-skeleton__line metrics-skeleton__line--label" />
                <Bone className="metrics-skeleton__line metrics-skeleton__line--value" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
